import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useGetApiAttachmentsQuery } from '../../../app/athenaApiGen';

export default function AttachmentsCell({ projectId }) {
  const { data: response, isLoading: loading, isError } = useGetApiAttachmentsQuery(
    {
      objectId: projectId,
      objectType: 'Project',
      pageSize: 100, // Get up to 100 attachments
    },
    {
      skip: !projectId, // Skip query if no projectId
    }
  );

  const attachments = response?.attachments || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const count = attachments.length;

  if (count === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%', color: 'text.secondary' }}>
        <AttachFileIcon fontSize="small" />
        <Typography variant="body2">0</Typography>
      </Box>
    );
  }

  return (
    <Tooltip
      title={
        <List dense sx={{ py: 0, m: 0 }}>
          {attachments.map((att) => (
            <ListItem key={att.attachmentId} sx={{ px: 1, py: 0.5 }}>
              <ListItemText
                primary={att.name || 'Unnamed'}
                secondary={att.description || ''}
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { fontWeight: 500 }
                }}
                secondaryTypographyProps={{ 
                  variant: 'caption'
                }}
              />
            </ListItem>
          ))}
        </List>
      }
      placement="left"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
              '&::before': {
                border: '1px solid',
                borderColor: 'divider',
              },
            },
            maxWidth: 400,
            '& .MuiListItemText-secondary': {
              color: 'text.secondary',
            },
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%', cursor: 'pointer' }}>
        <AttachFileIcon fontSize="small" color="primary" />
        <Typography variant="body2" color="primary" fontWeight={500}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
}

AttachmentsCell.propTypes = {
  projectId: PropTypes.string.isRequired,
};
